/*
 * Keep the customer on the product grid when Boost pagination is used, and put
 * them on it when a link arrives already filtered.
 *
 * On a collection template with sections above the grid (a featured collection,
 * a banner), clicking a Boost page number sent the customer to the top of the
 * page, scrolling back down to find the products they had just asked for.
 *
 * WHAT BOOST ACTUALLY DOES, measured in the browser
 *
 * Clicking a page control from a scroll position of 6800, near the foot of the
 * grid:
 *
 *   +614ms   scrollY 6800   nothing has moved yet
 *   +1322ms  scrollY 4593   the grid wrapper has been detached and replaced
 *   +1430ms  scrollY 1021
 *   +1608ms  scrollY  431
 *   +1806ms  scrollY  136
 *   +2007ms  scrollY   15
 *   +2205ms  scrollY    0   and it stays there
 *
 * That deceleration curve is an animated scroll to the top, and it belongs to
 * Boost: it starts about 1.3s after the click, once the re-render lands, and
 * takes roughly 900ms to run.
 *
 * Earlier versions of this file were built on the opposite conclusion - that
 * Boost never scrolls, and the position was lost to a browser scroll clamp as the
 * document shortened. That came from grepping Boost's bundle for scroll calls and
 * finding none. The flaw: only Boost's TAE core is inlined in the page. The app
 * bundle that renders the grid and owns the pagination is fetched at runtime and
 * is not in the page source at all, so its behaviour cannot be established that
 * way, and absence of evidence there was not evidence of absence.
 *
 * EXACTLY WHICH CALL, from a stack trace in the browser:
 *
 *   window.scrollTo({ top: 0, behavior: 'smooth' })
 *     <- DomHelper.scrollToTop                    (bc-widget-integration.js)
 *     <- FilterController.processPaginatingAction (filter.module.js)
 *
 * One identifiable call, made once per pagination action.
 *
 * THE APPROACH: SUPPRESS, LAND, HOLD, CONFIRM
 *
 *   1. SUPPRESS that call. While a pagination action is in flight, a wrapper
 *      around window.scrollTo swallows requests to scroll to the top of the
 *      document. Boost's glide therefore never starts, so there is no frame in
 *      which the page is anywhere unexpected. Everything else passes through.
 *
 *   2. LAND immediately. Scroll to the top of the grid straight away, while the
 *      previous page of products is still on screen. One instant move, at the
 *      moment the customer asked for it.
 *
 *   3. HOLD, as a backstop. A scroll listener puts the position back if something
 *      moves it anyway - a Boost update that scrolls by some other means, say.
 *      With suppression in place this normally has nothing to do.
 *
 *   4. CONFIRM when things go quiet, re-measuring in case the re-render shifted
 *      the layout, then stop suppressing.
 *
 *   5. FOLLOW for a while longer, absorbing anything that still shifts the grid
 *      - see THE SECOND PHASE below. Ends the moment the customer scrolls.
 *
 * Step 1 is what removes the flicker. Step 3 alone left one: undoing a scroll is
 * reactive, so the browser can paint Boost's position before the correction runs.
 * Not letting the scroll happen at all has no such window.
 *
 * Earlier attempts, all reasonable-looking, all wrong:
 *
 * - Repeated scrolls on a fixed schedule. The schedule ran out at 1000ms, about
 *   1200ms before Boost had finished, so Boost simply won. Any fixed schedule
 *   encodes a guess about someone else's animation.
 *
 * - Waiting for the page to go still and only then scrolling. Correct
 *   destination, unpleasant journey: the customer watched Boost's full ~900ms
 *   glide to the top before being snapped back to the grid.
 *
 * - Holding the position by undoing every scroll. Nearly right, but reactive, and
 *   the flicker of one painted frame was visible.
 *
 * WHAT IS ACTUALLY HELD: THE GRID, NOT A COORDINATE
 *
 * Step 3 holds "the top of the grid", re-measured every frame - not the scroll
 * position that was correct when the hold started.
 *
 * Those are the same number only while nothing above the grid changes size, and
 * on this store plenty does. collection.col-gaming-keyboards.json alone carries
 * three featured-collection rows above the grid, whose images and Klaviyo star
 * ratings arrive on their own schedule; Boost's toolbar and feature widgets do
 * the same. Every one of them pushes the grid further down the document, and a
 * coordinate captured before they arrived now points at whatever moved into that
 * space.
 *
 * The grid's own growth - the placeholder skeleton being swapped for real
 * products - is harmless by comparison: it all happens BELOW the top of the
 * anchor, which is the thing being measured.
 *
 * Holding a coordinate therefore produced the visible symptom that prompted this:
 * a correct-looking landing, then a wait, then a second jump. The wait was the
 * quiet window in step 4, because a re-measure only happened at release; the
 * second jump was that re-measure. Re-measuring every frame makes the grid stay
 * put while the page grows around it, and there is nothing left to correct at
 * release.
 *
 * A frame, not a poll on the interval: layout shifts land between frames, so a
 * 100ms poll shows them for up to six frames before putting them right. A rect
 * read and a comparison per frame is cheap, and it only runs while a hold is
 * active - a second or two per navigation.
 *
 * The browser's own scroll anchoring is not a substitute. It stands down around
 * programmatic scrolling, of which there is plenty here, and it re-picks its
 * anchor when the element it chose is removed - which Boost does to the whole
 * grid on every re-render.
 *
 * A shift is also counted as noise, so the page growing keeps the hold alive
 * rather than letting step 4 release into a layout that is still moving.
 *
 * THE SECOND PHASE, AND WHY THE HOLD ALONE IS NOT ENOUGH
 *
 * The hold ends when Boost goes quiet, and Boost goes quiet well before the page
 * does. Measured on the server HTML of one collection above the grid: 107 Klaviyo
 * favourite slots and 2 star-rating widgets, none of which reserve their space,
 * all of them filled in after a network round trip that has nothing to do with
 * Boost. Each one that lands moves the grid. Every image up there does declare
 * width and height, so images are not the problem; the injected widgets are.
 *
 * Ending at Boost's quiet therefore left the last of the jumping in place - it
 * simply happened after we had let go, which is why it looked intermittent: it
 * depends on whether Klaviyo answers before or after the hold expires.
 *
 * So the two jobs are separated:
 *
 *   HOLD    - while Boost is working, the grid goes where we put it, whatever
 *             else tries to scroll. This is the phase that fights.
 *
 *   FOLLOW  - afterwards, up to MAX_FOLLOW_MS, the ONLY thing that moves the
 *             page is a shift of the grid itself, and it moves by exactly as
 *             much as the grid moved. The grid keeps its place in the viewport,
 *             and anything else that scrolls - another script, a fragment link -
 *             is left alone, because the correction is a delta rather than a
 *             destination.
 *
 * Both end instantly when the customer scrolls. MAX_FOLLOW_MS is not a guess
 * about anyone else's animation - the mistake catalogued above - because nothing
 * is racing it: it is just how long we are willing to keep tidying up after a
 * page that is still filling itself in.
 *
 * WHAT "QUIET" MEANS IN STEP 3
 *
 * Not the scroll position - we are holding that, so it looks still even while
 * Boost is fighting us for it. Quiet means all three of: no DOM changes in the
 * grid's section, no change in document height, and no corrections made by the
 * hold. That last one is the useful signal, because a correction is direct
 * evidence that something else is still trying to move the page.
 *
 * Releasing on DOM stillness alone would be too early: Boost's re-render mutates
 * at ~1.3s and then animates in silence until ~2.2s, so the DOM goes quiet with
 * 900ms of scrolling still to come.
 *
 * OTHER THINGS LEARNED THE HARD WAY
 *
 * - Boost v3 fires no boost:rendered / boost:filter:after / boost:search:after.
 *   Every "boost:" in a rendered page is the theme's own addEventListener in
 *   layout/theme.liquid; the only event Boost dispatches is
 *   boost-sd-enable-product-filter-fallback, on window. Those listeners are v2
 *   leftovers, and this file does not use them.
 *
 * - The grid's pagination controls carry NO data-page attribute. That attribute
 *   appears only in the Liquid templates Boost inlines for its search suggestion
 *   panel, which do not describe the grid. Match on class names and intent.
 *
 * - Boost replaces .boost-sd__filter-product-list and everything inside it, so no
 *   element reference from within the grid survives a re-render. Anchor to the
 *   Shopify-owned wrapper outside it, which Boost renders into and never
 *   replaces.
 *
 * ARRIVING ON A FILTERED URL
 *
 * The same "start at the products" applies to a link that lands on an already
 * filtered grid. The spec pills on a product page (snippets/pill-map.liquid)
 * point at URLs like /collections/gaming-keyboards?label4=TKL, and that
 * collection carries five sections above its grid, so the customer arrived at a
 * banner rather than at the keyboards they had asked for. On load, a URL carrying
 * a filter is treated exactly like a deep-linked ?page=2: hold, and land on the
 * grid.
 *
 * "Carrying a filter" is decided by ELIMINATION - any query param that is not a
 * known non-filter counts as one. Boost's facet aliases are named in the app
 * admin, not in this repo, and can be any word at all: label4, hot_swappable and
 * custom_range_slider_2 are all live on this store. There is no pattern to match
 * on, so the list below is of what to ignore. See NOT_A_FILTER.
 *
 * The landing itself is immediate even though Boost renders the grid client-side,
 * because Boost's app block ships a skeleton in the server HTML: inside
 * .boost-sd__filter-product-list sit .boost-sd__product-list-placeholder and a
 * .boost-sd__pagination, which is enough for findAnchor to recognise a grid and
 * climb to its section. So there is something to measure before Boost has
 * rendered anything.
 *
 * What arrives late is everything AROUND that skeleton, which is what the hold is
 * really for. See below.
 *
 * Clicking a filter in Boost's own sidebar is still left alone: it re-renders in
 * place without a navigation, and changing that is a behaviour change beyond what
 * was asked for.
 *
 * Self-wiring: exposes no refresh(), needs no entry in theme.liquid's refresh
 * block.
 */
(function () {
  /* Any pagination control: page numbers and prev/next. Class prefix rather than
     exact class, so the icon <span> inside prev/next counts and a markup tweak
     does not silently stop this working. */
  var CONTROL = '[class*="boost-sd__pagination-number"], [class*="boost-sd__pagination-button"]';

  /* ...but not the page we are on, the "..." ellipsis, or anything disabled: none
     of those re-render. closest() from the hit, so the icon inside a disabled
     button is covered too. */
  var NOT_A_JUMP =
    '.boost-sd__pagination-number--active,' +
    '.boost-sd__pagination-number--disabled,' +
    '[disabled],' +
    '[aria-disabled="true"]';

  /* Outermost first, because we need a wrapper Boost does NOT replace. Measured:
     .boost-sd__filter-product-list detaches during the re-render, so anchoring
     there loses the element. .shopify-section and .shopify-app-block are rendered
     by Shopify, and Boost renders inside them. */
  var STABLE_ANCHORS = ['.shopify-section', '.shopify-app-block', '.boost-sd__filter-product-list'];

  /* Used only when there is no click to walk up from (a deep-linked ?page=2). */
  var GRID_HINTS = '.boost-sd__filter-product-list, .boost-sd-container';

  /* Query params that do NOT mean "show me a filtered grid": Shopify plumbing,
     the grid controls that are not facets, and ad-network click ids. Anything
     else counts - see ARRIVING ON A FILTERED URL above for why this is a list of
     exclusions rather than a list of facet names.

     `q` is deliberately in here. It is the header search box at least as often as
     it is a pill, and on the search template the grid is the first section, so
     there is nothing above it to scroll past.

     `page` is in here because it has its own, stricter rule in onLoad: page 1 is
     not a jump. */
  var NOT_A_FILTER = [
    'page', 'sort_by', 'sort', 'view', 'q', 'type', 'variant', 'section_id',
    'preview_theme_id', 'customer_posted', 'redirect', 'discount', 'key', 'ref',
    'gclid', 'gclsrc', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid',
    'twclid', 'yclid', 'epik', 'irclickid', 'srsltid', 'li_fat_id', 's_kwcid'
  ];

  /* Whole families of the same: utm_*, mc_* (Mailchimp), gad_*, and Shopify's own
     underscore-led ones (_ab, _fd, _sc, _kx, and the _pos/_sid/_ss trio on links
     out of a search). */
  var NOT_A_FILTER_PREFIXES = ['utm_', 'mc_', 'gad_', '_'];

  var POLL_MS = 100;
  /* Quiet required before releasing the hold - but only once something has
     actually happened. See NOTHING HAS HAPPENED YET below. */
  var QUIET_MS = 400;
  /* How long to keep holding when the re-render never starts at all. */
  var NO_ACTIVITY_MS = 3000;
  /* Hard cap, so a page that never settles cannot be held for ever. */
  var MAX_HOLD_MS = 6000;
  /* How long to go on absorbing shifts after the hold ends. Generous, because
     the phase costs a rect read per frame and moves the page only when the grid
     itself moves - and the widgets it is waiting for are slow. */
  var MAX_FOLLOW_MS = 10000;
  var TOLERANCE = 8;

  /* A wheel, touch or key means the customer has taken over. */
  var USER_EVENTS = ['wheel', 'touchmove', 'keydown'];

  var timer = 0;
  var raf = 0;
  var observer = null;
  /* The element the hold is anchored to. Module-level rather than passed around,
     because the per-frame pin resolves it lazily: Boost replaces the grid on every
     re-render, and a hold can start before there is one to find. */
  var anchorEl = null;
  /* window.scrollTo as it was before we wrapped it. Everything in this file
     scrolls through this, so our own scrolling is never suppressed and does not
     depend on who else has wrapped the API. */
  var nativeScrollTo = null;
  var suppressing = false;
  var userMoved = false;
  /* The document position of the top of the grid, as of the last measurement.
     Null means "not holding anything yet" - either no grid has appeared, or the
     customer has taken over. */
  var held = null;
  var lastNoiseAt = 0;
  var sawNoise = false;
  /* When the follow phase gives up. Zero when it is not running. */
  var followUntil = 0;

  function offset() {
    /* --header-height is set on <html> in px by assets/header.js. */
    var raw = window.getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    var px = parseFloat(raw);
    if (!isNaN(px) && px > 0) return px + 16;
    var header = document.querySelector('.header');
    if (header) {
      var h = header.getBoundingClientRect().height;
      if (h > 0) return h + 16;
    }
    return 96;
  }

  function isFilter(name) {
    var n = String(name || '').toLowerCase();
    if (!n) return false;
    for (var i = 0; i < NOT_A_FILTER_PREFIXES.length; i++) {
      if (n.indexOf(NOT_A_FILTER_PREFIXES[i]) === 0) return false;
    }
    return NOT_A_FILTER.indexOf(n) === -1;
  }

  /* Reads every param, not just the first: a pill is one facet, but a link copied
     out of a filtered grid can carry several, or a tracking tag alongside one. */
  function anyFilterIn(params) {
    var found = false;
    params.forEach(function (value, name) {
      if (isFilter(name)) found = true;
    });
    return found;
  }

  function visible(el) {
    return !!(el && el.getClientRects && el.getClientRects().length);
  }

  function scrollWindow(top) {
    (nativeScrollTo || window.scrollTo).call(window, { top: top, behavior: 'auto' });
  }

  /* Is this call trying to put the document at its very top? Both call shapes:
     scrollTo({ top: n, ... }) and the positional scrollTo(x, y). */
  function targetsTop(args) {
    if (args.length === 1 && args[0] && typeof args[0] === 'object') {
      return typeof args[0].top === 'number' && args[0].top <= TOLERANCE;
    }
    return args.length > 1 && typeof args[1] === 'number' && args[1] <= TOLERANCE;
  }

  /* Wrap window.scrollTo once, lazily, on the first pagination click. Wrapping
     once and gating on a flag rather than swapping the function in and out: a
     swap races anything else that wraps the same API, and leaving a wrapper in
     place that is inert unless suppressing is set costs nothing.

     Only scroll-to-top is swallowed, and only while a pagination action is in
     flight. Anything else - including our own landing, which goes through
     nativeScrollTo - passes straight through. */
  function installSuppressor() {
    if (nativeScrollTo) return;
    nativeScrollTo = window.scrollTo;
    window.scrollTo = function () {
      if (suppressing && targetsTop(arguments)) {
        /* Count it: Boost trying to scroll is proof it is still working, which
           the quiet test in holdUntilQuiet() waits out. */
        noise();
        return;
      }
      return nativeScrollTo.apply(window, arguments);
    };
  }

  function noise() {
    lastNoiseAt = Date.now();
    sawNoise = true;
  }

  function stableAnchorFrom(el) {
    for (var i = 0; i < STABLE_ANCHORS.length; i++) {
      var found = el.closest(STABLE_ANCHORS[i]);
      if (found) return found;
    }
    return null;
  }

  /* No click to work from: find a visible grid and climb to its stable wrapper.
     Prefers one that actually holds products, so an empty or offscreen wrapper is
     not mistaken for the grid. */
  function findAnchor() {
    var hints = document.querySelectorAll(GRID_HINTS);
    for (var i = 0; i < hints.length; i++) {
      if (!visible(hints[i])) continue;
      if (!hints[i].querySelector('.boost-sd__pagination, [class*="product-list"], [class*="product-item"]')) continue;
      return stableAnchorFrom(hints[i]) || hints[i];
    }
    return null;
  }

  /* The customer has taken over. Both phases end here, and this is the only exit
     the follow phase has other than its deadline - it runs with no interval to
     clean up after it. */
  function onUserMove() {
    userMoved = true;
    release();
  }

  /* Put the position back whenever anything other than the customer moves it, and
     record it as noise: a correction is direct evidence that something else is
     still trying to move the page, which is what step 3 waits out. The tolerance
     check keeps our own correction from re-entering this handler in a loop.

     Attached for the hold only. The follow phase deliberately does not override
     other scrolling - see THE SECOND PHASE above. */
  function onScroll() {
    if (held === null || userMoved) return;
    if (Math.abs(window.scrollY - held) <= TOLERANCE) return;
    noise();
    scrollWindow(held);
  }

  /* Kept separate because they outlive each other: the user listeners run for
     both phases, the scroll listener only for the hold. */
  function listenUser(on) {
    for (var i = 0; i < USER_EVENTS.length; i++) {
      if (on) window.addEventListener(USER_EVENTS[i], onUserMove, { passive: true });
      else window.removeEventListener(USER_EVENTS[i], onUserMove, { passive: true });
    }
  }

  function listenScroll(on) {
    if (on) window.addEventListener('scroll', onScroll, { passive: true });
    else window.removeEventListener('scroll', onScroll, { passive: true });
  }

  /* Stop fighting for the position: no more suppression, no more watching, no
     more undoing other people's scrolling. The follow phase carries on. */
  function endHold() {
    suppressing = false;
    if (timer) {
      window.clearInterval(timer);
      timer = 0;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    listenScroll(false);
  }

  /* Stop touching the page at all. */
  function endFollow() {
    held = null;
    followUntil = 0;
    if (raf) {
      window.cancelAnimationFrame(raf);
      raf = 0;
    }
    listenUser(false);
  }

  function release() {
    endHold();
    endFollow();
  }

  /* The anchor, re-resolved when it has gone (Boost replaces the grid on a
     re-render) or was never found (a template that ships no Boost skeleton, so
     there is nothing to recognise until the render lands). Cached, so the
     per-frame pin is a property read rather than a document query. */
  function resolveAnchor() {
    /* Connected is enough. Mid-re-render Boost empties the grid, and findAnchor
       requires products, so asking it again there would answer null and throw away
       a perfectly good anchor - the wrapper is still in the document and its top is
       still the right thing to measure. Only ever replaced by something better. */
    if (anchorEl && anchorEl.isConnected) return anchorEl;
    var found = findAnchor();
    if (found) anchorEl = found;
    return anchorEl;
  }

  /* Where the top of the grid is NOW, in document coordinates. Null when there is
     no grid to measure. Read fresh every time - see WHAT IS ACTUALLY HELD above. */
  function targetTop() {
    var el = resolveAnchor();
    if (!visible(el)) return null;
    var top = el.getBoundingClientRect().top + window.scrollY - offset();
    return top < 0 ? 0 : top;
  }

  /* Scroll to the top of the grid and report where that was, so the caller can
     hold it. Instant: a smooth scroll would be an animation of our own, for Boost
     to interrupt. */
  function land() {
    if (userMoved) return null;
    var top = targetTop();
    if (top === null) return null;
    if (Math.abs(window.scrollY - top) > TOLERANCE) {
      scrollWindow(top);
    }
    return top;
  }

  /* Watch the grid for the quiet test. Attached here rather than at the start of
     the hold because on a fresh load there is nothing to watch yet, and the
     element Boost renders into is the useful one. */
  function watch(el) {
    if (observer || !el || !('MutationObserver' in window)) return;
    observer = new MutationObserver(noise);
    observer.observe(el, { childList: true, subtree: true });
  }

  /* One frame of either phase: measure the grid, and put the page right.

     Measuring rather than remembering is the whole point. A shift above the grid
     changes where the grid is without the customer doing anything, so the position
     worth keeping changes with it; treating that as a scroll to be undone would
     pin the page to a coordinate the products have already left. */
  function pinOnce() {
    if (userMoved) return;
    var want = targetTop();
    if (want === null) return;
    watch(anchorEl);

    var shift = 0;
    if (held === null) {
      /* First measurement: a hold that began before there was a grid to measure. */
      held = want;
    } else if (Math.abs(want - held) > TOLERANCE) {
      /* Something above the grid changed size. Count it as noise: something is
         still arriving, which step 4 should wait out rather than release into. */
      shift = want - held;
      held = want;
      noise();
    }

    if (suppressing) {
      /* HOLD: the grid goes where we put it, whatever else is trying to scroll. */
      if (Math.abs(window.scrollY - held) > TOLERANCE) {
        scrollWindow(held);
      }
    } else if (shift) {
      /* FOLLOW: absorb the shift, and nothing else. Moving by exactly as much as
         the grid moved leaves it where it was in the viewport while leaving any
         scrolling someone else did intact. */
      scrollWindow(window.scrollY + shift);
    }
  }

  function pin() {
    raf = 0;
    if (userMoved) return;
    pinOnce();
    if (userMoved) return;
    if (!suppressing && Date.now() >= followUntil) {
      endFollow();
      return;
    }
    raf = window.requestAnimationFrame(pin);
  }

  /* Hold the current position until the page stops being moved and rebuilt, then
     re-measure and let go. */
  function holdUntilQuiet(anchor) {
    release();
    installSuppressor();
    suppressing = true;
    userMoved = false;
    lastNoiseAt = Date.now();
    sawNoise = false;
    anchorEl = anchor || null;
    followUntil = Date.now() + MAX_FOLLOW_MS;
    listenUser(true);
    listenScroll(true);
    watch(anchorEl);

    /* The pin is what holds the position; the interval below only decides when to
       stop. Where there is no requestAnimationFrame the interval calls pinOnce
       itself - a correction per 100ms rather than per frame, and no follow phase
       at all once the interval is cleared. Degraded, not broken. */
    if (window.requestAnimationFrame) {
      raf = window.requestAnimationFrame(pin);
    }

    var started = Date.now();
    var lastH = document.documentElement.scrollHeight;

    timer = window.setInterval(function () {
      if (userMoved) {
        release();
        return;
      }

      if (!raf) pinOnce();

      var h = document.documentElement.scrollHeight;
      if (h !== lastH) {
        lastH = h;
        noise();
      }

      /* NOTHING HAS HAPPENED YET.
         Boost sits idle for ~1.3s after the click, so for the first stretch the
         page is quiet simply because the re-render has not begun. Releasing on
         that quiet would drop the hold before Boost ever moves the page - which
         is exactly the bug this file has been chasing. So the quiet test only
         applies once we have seen something; until then, hold and wait. */
      var now = Date.now();
      var settled = sawNoise
        ? now - lastNoiseAt >= QUIET_MS
        : now - started >= NO_ACTIVITY_MS;

      if (settled || now - started > MAX_HOLD_MS) {
        /* End the hold, not the follow: Boost is done, but the widgets above the
           grid may not be. */
        endHold();
        /* One last measurement. The pin has been keeping up frame by frame, so
           this normally moves nothing - it matters when the hold ends on the hard
           cap with the page still settling. */
        var top = land();
        if (top !== null) held = top;
      }
    }, POLL_MS);
  }

  function onClick(event) {
    var el = event.target;
    if (!el || typeof el.closest !== 'function') return;
    var hit = el.closest(CONTROL);
    if (!hit || hit.closest(NOT_A_JUMP)) return;

    /* Anchor from the clicked control, never a global lookup: a page can hold
       several Boost grids, and this is the one the customer is using. */
    var anchor = stableAnchorFrom(hit);
    if (!anchor) return;

    /* Order matters. holdUntilQuiet() installs the listeners and clears any
       previous hold, so the position to hold is taken after it. */
    holdUntilQuiet(anchor);
    held = land();
  }

  /* A link that arrives asking for a particular set of products - a spec pill's
     facet URL, a shared or bookmarked ?page=2 - should land on the grid rather
     than the top of the template. Skipped when the browser has already restored a
     position, so reloading part-way down a grid does not yank the customer to its
     top. */
  function onLoad() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var page = parseInt(params.get('page'), 10);
    var deepLinkedPage = !!page && page >= 2;
    if (!deepLinkedPage && !anyFilterIn(params)) return;
    if (window.scrollY > TOLERANCE) return;
    var anchor = findAnchor();
    holdUntilQuiet(anchor);
    held = land();
  }

  /* Capture: Boost owns these controls and may stop propagation on its own
     handler, which would hide the click from a bubble-phase listener. */
  document.addEventListener('click', onClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onLoad);
  } else {
    onLoad();
  }
})();
